import { useState, useMemo, useEffect } from 'react';
import { CloudType } from '../../types/policies';
import {
  Send,
  Sparkles,
  ShieldBan,
  Wrench,
  Loader2,
  Check,
  X,
  RotateCcw,
  Shield,
  ArrowUpRight,
  ShieldAlert,
  Cloud,
  Brain,
  Database,
  Globe,
  GitMerge,
  Key,
  Server,
  UserX,
  Unlock,
  Activity,
  OctagonAlert,
  CornerDownLeft,
} from 'lucide-react';
import { Button } from '../../../../ui/common-ui/button';
import { Checkbox } from '../../../../ui/common-ui/checkbox';
import { Badge } from '../../../../ui/common-ui/badge';
import { toast } from 'sonner';
import { PolicyCard } from './PolicyCard';
import { PolicyDetailsEdit } from './PolicyDetailsEdit';
import { CreatePolicySelectors } from './CreatePolicySelectors';
import { PolicyAlertLinker } from './PolicyAlertLinker';
import { Policy, PolicyFeasibility } from '../../types/policies';
import { generatePolicy, savePolicy, fetchLlmTaskStatus, getApiErrorMessage } from '../../services/api';
import { normalizeCloudPolicies, normalizeCloudPolicy } from '../../lib/policyTransforms';
import { getResourceOptionsForPolicy } from '../../lib/resourceTargeting';
import { fetchAlertSelectableItems, saveAlerts } from '../../../CloudAlertManagement/services/api';
import { AlertCard } from '../../../CloudAlertManagement/components/alerts/AlertCard';
import { AlertDetailsEdit } from '../../../CloudAlertManagement/components/alerts/AlertDetailsEdit';
import {
  LLM_PROVIDER_KEY_NOT_CONFIGURED_MESSAGE,
  LLM_TASK_ALREADY_IN_PROGRESS_MESSAGE,
  useLLMProviderAvailability,
} from '../../../hooks/useLLMProviderAvailability';


type PolicyType = 'guardrail' | 'remediation';
type SelectedPolicyType = PolicyType | 'automatic';

interface CreatePolicyChatProps {
  selectedCloud: CloudType;
  tenantName?: string;
  cloudId?: string;
  initialType?: SelectedPolicyType;
  initialLinkedAlerts?: string[];
  onPoliciesAdded?: (policies: Policy[]) => void;
}

export type SuggestionItem = {
  title: string;
  description: string;
  prompt: string;
  icon: typeof Brain;
};

type SuggestionMap = Record<CloudType, Record<PolicyType, SuggestionItem[]>>;

const suggestionsByCloudAndType: SuggestionMap = {
  AWS: {
    guardrail: [
      { title: 'S3 Production Protection', description: 'Prevent the deletion of critical S3 buckets in production environments.', prompt: 'Block deletion of any S3 bucket in production', icon: Database },
      { title: 'Block Public S3', description: 'Ensure no new S3 buckets can be configured for public access.', prompt: 'Prevent public access on any new S3 bucket', icon: Globe },
      { title: 'IAM Policy Lockdown', description: 'Restrict IAM policy modifications to administrator roles only.', prompt: 'Block IAM policy changes by non-admin users', icon: ShieldAlert },
      { title: 'Restrict Open Ingress', description: 'Deny any security group rule that allows global access (0.0.0.0/0).', prompt: 'Prevent security group rules allowing 0.0.0.0/0 ingress', icon: ShieldAlert },
    ],
    remediation: [
      { title: 'Downgrade Privileges', description: 'Automatically reduce permissions when a privilege escalation is detected.', prompt: 'Downgrade permissions on privilege escalation alert', icon: GitMerge },
      { title: 'Revoke Access Keys', description: 'Disable compromised access keys automatically upon suspicious API activity.', prompt: 'Revoke access keys on suspicious API activity', icon: Key },
      { title: 'Quarantine EC2', description: 'Isolate EC2 instances immediately upon malware detection.', prompt: 'Quarantine EC2 instance on malware detection', icon: Server },
      { title: 'Disable Unused Accounts', description: 'Automatically disable IAM users that have been inactive for over 90 days.', prompt: 'Disable inactive IAM users after 90 days', icon: UserX },
    ],
  },
  GCP: {
    guardrail: [
      { title: 'Compute Engine Protection', description: 'Prevent accidental deletion of critical VM instances.', prompt: 'Block deletion of production Compute Engine instances', icon: Server },
      { title: 'Block Public Storage', description: 'Ensure no Cloud Storage buckets can be made public.', prompt: 'Prevent public access to Cloud Storage buckets', icon: Cloud },
      { title: 'Restrict Firewall Rules', description: 'Deny firewall rules that allow global SSH or RDP access.', prompt: 'Block firewall rules allowing 0.0.0.0/0 for SSH/RDP', icon: ShieldAlert },
      { title: 'Service Account Limits', description: 'Prevent non-admins from creating new service account keys.', prompt: 'Block service account key creation by non-admins', icon: Key },
    ],
    remediation: [
      { title: 'Revoke Service Account', description: 'Disable service accounts immediately upon detecting anomalous activity.', prompt: 'Disable service account on anomalous activity alert', icon: UserX },
      { title: 'Isolate Compromised VM', description: 'Move compromised VMs to a quarantine VPC network.', prompt: 'Move compromised VM to quarantine network', icon: Server },
      { title: 'Remove Public Access', description: 'Automatically revert buckets back to private if made public.', prompt: 'Revert Cloud Storage buckets to private', icon: Globe },
      { title: 'Revoke API Keys', description: 'Delete API keys that have been leaked or exposed.', prompt: 'Delete leaked API keys immediately', icon: Key },
    ],
  },
  AZURE: {
    guardrail: [
      { title: 'VM Deletion Lock', description: 'Prevent the deletion of production Azure virtual machines.', prompt: 'Block deletion of production virtual machines', icon: Server },
      { title: 'Block Public Blobs', description: 'Ensure Blob Storage containers cannot be accessed publicly.', prompt: 'Prevent public access to Blob Storage containers', icon: Database },
      { title: 'Restrict NSG Rules', description: 'Deny Network Security Groups from allowing global internet access.', prompt: 'Block NSG rules allowing internet ingress to any port', icon: ShieldAlert },
      { title: 'Key Vault Protection', description: 'Prevent unauthorized users from modifying Key Vault access policies.', prompt: 'Block Key Vault access policy modifications', icon: Key },
    ],
    remediation: [
      { title: 'Revoke Managed Identity', description: 'Disable managed identities upon detecting anomalous behavior.', prompt: 'Disable managed identity on anomalous activity', icon: Unlock },
      { title: 'Isolate Azure VM', description: 'Disconnect compromised VMs from the primary virtual network.', prompt: 'Isolate compromised VM from virtual network', icon: Server },
      { title: 'Remove Guest Users', description: 'Automatically remove guest users exhibiting suspicious sign-in patterns.', prompt: 'Remove guest users on suspicious sign-in', icon: UserX },
      { title: 'Rotate Secrets', description: 'Trigger automatic rotation of Key Vault secrets if a breach is detected.', prompt: 'Rotate Key Vault secrets on breach detection', icon: Activity },
    ],
  },
};

const policyTypes: {
  type: SelectedPolicyType;
  label: string;
  icon: typeof Shield;
  description: string;
  color: string;
}[] = [
  {
    type: 'automatic',
    label: 'Automatic',
    icon: Sparkles,
    description: 'System decides based on your description',
    color: 'amber',
  },
  {
    type: 'guardrail',
    label: 'Guardrail',
    icon: ShieldBan,
    description: 'Immediate prevention & enforcement',
    color: 'rose',
  },
  {
    type: 'remediation',
    label: 'Remediation',
    icon: Wrench,
    description: 'Triggered after alerts or activity',
    color: 'blue',
  },
];

const LOADING_TEXTS = [
  'Analyzing policy description...',
  'Evaluating cloud permissions...',
  'Mapping enforcement rules...',
  'Building condition logic...',
  'Configuring automated actions...',
  'Validating policy parameters...',
  'Optimizing enforcement scope...',
  'Finalizing policy...',
];

export function CreatePolicyChat({ selectedCloud, tenantName, cloudId, initialType, initialLinkedAlerts, onPoliciesAdded }: CreatePolicyChatProps) {
  const [prompt, setPrompt] = useState('');
  const [selectedType, setSelectedType] = useState<SelectedPolicyType>(initialType || 'automatic');
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [displayedSuggestions, setDisplayedSuggestions] = useState<SuggestionItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [generatedPolicies, setGeneratedPolicies] = useState<Policy[]>([]);
  const [generatedAlerts, setGeneratedAlerts] = useState<Record<string, any>>({});
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null);
  const [previewAlert, setPreviewAlert] = useState<any | null>(null);
  const [selectedPolicyIds, setSelectedPolicyIds] = useState<Set<string | number>>(new Set());
  const [previewPolicy, setPreviewPolicy] = useState<Policy | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [rejection, setRejection] = useState<(PolicyFeasibility & { prompt: string }) | null>(null);
  const { hasConfiguredLLMProviderKey } = useLLMProviderAvailability();

  // LLM task status polling (every 2s) to gate the send button
  const [isCreatePolicyTaskInProgress, setIsCreatePolicyTaskInProgress] = useState(false);

  useEffect(() => {
    if (!tenantName || !cloudId) {
      setIsCreatePolicyTaskInProgress(false);
      return;
    }
    let active = true;
    const poll = async () => {
      try {
        const statuses = await fetchLlmTaskStatus(tenantName, cloudId);
        if (!active) return;
        const task = statuses.find((s) => s.llmTask === 'CREATE_POLICY');
        setIsCreatePolicyTaskInProgress(task?.status === 'IN_PROGRESS');
      } catch {
        // ignore polling errors
      }
    };
    poll();
    const intervalId = setInterval(poll, 2000);
    return () => {
      active = false;
      clearInterval(intervalId);
    };
  }, [tenantName, cloudId]);

  // Pre-configuration state
  const [preSelectedUsers, setPreSelectedUsers] = useState<string[]>([]);
  const [preSelectedServiceAccounts, setPreSelectedServiceAccounts] = useState<string[]>([]);
  const [preSelectedResources, setPreSelectedResources] = useState<string[]>([]);
  const [preLinkedAlerts, setPreLinkedAlerts] = useState<string[]>(initialLinkedAlerts || []);

  // Loading progress state
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingTextIndex, setLoadingTextIndex] = useState(0);
  const [isCompletingAnimation, setIsCompletingAnimation] = useState(false);
  const [pendingPolicies, setPendingPolicies] = useState<Policy[] | null>(null);
  const [pendingAlerts, setPendingAlerts] = useState<Record<string, any> | null>(null);

  const currentSuggestions = useMemo(() => {
    if (selectedType === 'automatic') {
      const all = [
        ...suggestionsByCloudAndType[selectedCloud]?.guardrail || [],
        ...suggestionsByCloudAndType[selectedCloud]?.remediation || [],
      ];
      return all.slice(0, 4);
    }
    return suggestionsByCloudAndType[selectedCloud]?.[selectedType] || [];
  }, [selectedCloud, selectedType]);

  useEffect(() => {
    setDisplayedSuggestions(currentSuggestions);
  }, [currentSuggestions]);

  // Loading progress animation
  useEffect(() => {
    if (!isLoading || isCompletingAnimation) return;

    const interval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 90) return prev;
        return prev + 10;
      });
      setLoadingTextIndex(prev => {
        if (prev >= 7) return prev;
        return prev + 1;
      });
    }, 1200);

    return () => clearInterval(interval);
  }, [isLoading, isCompletingAnimation]);

  // Handle completion animation
  useEffect(() => {
    if (pendingPolicies && isCompletingAnimation) {
      setLoadingProgress(100);

      const timer = setTimeout(() => {
        setGeneratedPolicies(prev => {
          const combined = [...pendingPolicies, ...prev];
          // Simple deduplication if IDs conflict
          const seen = new Set();
          return combined.filter(p => {
            if (seen.has(p.id)) return false;
            seen.add(p.id);
            return true;
          });
        });
        if (pendingAlerts) {
          setGeneratedAlerts(prev => ({ ...prev, ...pendingAlerts }));
          const newAlertIds = Object.keys(pendingAlerts);
          if (newAlertIds.length > 0) {
            setSelectedAlertId(newAlertIds[0]);
          }
        }
        setIsLoading(false);
        setIsCompletingAnimation(false);
        setPendingPolicies(null);
        setPendingAlerts(null);
        setLoadingProgress(0);
        setLoadingTextIndex(0);
        toast.success(`Generated ${pendingPolicies.length} policy${pendingPolicies.length !== 1 ? 'ies' : 'y'}`);
      }, 800);

      return () => clearTimeout(timer);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [pendingPolicies, isCompletingAnimation]);

  const sendPromptBlockedReason = isCreatePolicyTaskInProgress
    ? LLM_TASK_ALREADY_IN_PROGRESS_MESSAGE
    : !hasConfiguredLLMProviderKey
      ? LLM_PROVIDER_KEY_NOT_CONFIGURED_MESSAGE
      : '';
  const isSendPromptDisabled = !prompt.trim() || isLoading || isCreatePolicyTaskInProgress || !hasConfiguredLLMProviderKey;
  const isPromptInputDisabled = isLoading || isCreatePolicyTaskInProgress || !hasConfiguredLLMProviderKey;

  const generatedAlertKey = (alert: any, fallback: string): string => {
    return String(alert?.alertId || alert?.id || fallback);
  };

  const buildAlertSavePayload = (alert: any) => ({
    title: alert.title || 'Untitled Alert',
    severity: alert.severity || 'Medium',
    alertType: alert.alertType || alert.type || 'BEHAVIOUR',
    info: alert.info || {},
    steps: alert.steps || {},
    mitre: alert.mitre || {},
    isFlagged: alert.isFlagged ?? alert.isFlaged ?? false,
    isDefault: alert.isDefault ?? false,
    users: alert.users || [],
    resource: alert.resource || [],
    timeWindow: alert.timeWindow || []
  });

  const stopLoading = () => {
    setIsLoading(false);
    setLoadingProgress(0);
    setLoadingTextIndex(0);
  };

  const handleSubmit = async (promptOverride?: string) => {
    const submittedPrompt = (promptOverride ?? prompt).trim();
    if (!submittedPrompt || isLoading || isCreatePolicyTaskInProgress || !hasConfiguredLLMProviderKey) return;

    setRejection(null);
    setShowSuggestions(false);
    setIsLoading(true);
    setLoadingProgress(0);
    setLoadingTextIndex(0);

    try {
      if (!tenantName || !cloudId) {
        toast.error('Please select a tenant and resource first');
        setIsLoading(false);
        return;
      }

      const response = await generatePolicy({
        tenantName,
        cloudId,
        cloudType: selectedCloud,
        userPrompt: submittedPrompt,
        cloudPolicyType: selectedType === 'automatic' ? undefined : selectedType === 'guardrail' ? 'GUARDRAIL' : 'REMEDIATION',
        policyTargetTypes: undefined,
        includedUsers: preSelectedUsers,
        includedNHIs: preSelectedServiceAccounts,
        includedResources: preSelectedResources,
        linkedAlerts: preLinkedAlerts,
      });

      if (response.feasibility?.feasible === false) {
        setRejection({ ...response.feasibility, prompt: submittedPrompt });
        setPrompt(submittedPrompt);
        stopLoading();
        return;
      }

      if (!response.policy || response.policy.length === 0) {
        toast.error(response.feasibility?.reason || 'No policy could be generated from this request. Try describing the targets more precisely.');
        setPrompt(submittedPrompt);
        stopLoading();
        return;
      }

      if (response.feasibility?.reason) {
        toast.info(response.feasibility.reason);
      }

      let selectableItems: any = null;
      try {
        selectableItems = await fetchAlertSelectableItems(tenantName, cloudId, selectedCloud);
      } catch (e) {
        console.error('Failed to fetch selectable items for mapping:', e);
      }

      const newPolicies = normalizeCloudPolicies(response.policy || []).map((p: any, i: number) => {
        let mappedIncludedUsers = p.includedUsers || [];
        let mappedExcludedUsers = p.excludedUsers || [];
        let mappedIncludedSAs = p.includedNHIs || [];
        let mappedExcludedSAs = p.excludedNHIs || [];
        let mappedIncludedResources = p.includedResources || [];
        let mappedExcludedResources = p.excludedResources || [];

        const mapTargets = (targets: string[], options: string[]) => {
            const newSet = new Set<string>();
            for (const t of targets) {
                if (t === "" || t === "*") { newSet.add(t); continue; }
                let matched = false;
                for (const s of options) {
                    if (s.includes(t)) { newSet.add(s); matched = true; }
                }
                if (!matched) newSet.add(t);
            }
            return Array.from(newSet);
        };

        if (selectableItems) {
            const userOptions = Object.values(selectableItems.users || {}).flat() as string[];
            mappedIncludedUsers = mapTargets(mappedIncludedUsers, userOptions);
            mappedExcludedUsers = mapTargets(mappedExcludedUsers, userOptions);

            const saOptions = selectedCloud === 'AWS' ? (selectableItems.awsIamRole || []) : (selectableItems.serviceAccount || selectableItems.serviceAccounts || []);
            mappedIncludedSAs = mapTargets(mappedIncludedSAs, saOptions);
            mappedExcludedSAs = mapTargets(mappedExcludedSAs, saOptions);

            const resourceOptions = getResourceOptionsForPolicy(selectableItems, p.policyTargetTypes, p.cloudType || selectedCloud);
            mappedIncludedResources = mapTargets(mappedIncludedResources, resourceOptions);
            mappedExcludedResources = mapTargets(mappedExcludedResources, resourceOptions);
        }

        return {
          ...p,
          id: p.id || p.policyId || `new-pol-${Date.now()}-${i}`,
          includedUsers: mappedIncludedUsers,
          excludedUsers: mappedExcludedUsers,
          includedNHIs: mappedIncludedSAs,
          excludedNHIs: mappedExcludedSAs,
          includedResources: mappedIncludedResources,
          excludedResources: mappedExcludedResources,
        };
      });

      // Map alerts if any are returned for Remediation policies
      const newAlertsMap: Record<string, any> = {};
      if (response.alert && Array.isArray(response.alert)) {
        response.alert.forEach((a: any, i: number) => {
          const alertId = generatedAlertKey(a, `new-alr-${Date.now()}-${i}`);
          newAlertsMap[alertId] = { 
            ...a, 
            id: alertId, 
            isEnabled: true,
            cloud: a.cloud || selectedCloud,
            steps: a.steps || {},
            mitre: a.mitre || []
          };
        });
      }

      setPendingPolicies(newPolicies);
      setPendingAlerts(newAlertsMap);
      setIsCompletingAnimation(true);
      setPrompt('');
    } catch (error) {
      console.error('Failed to generate policies:', error);
      toast.error(getApiErrorMessage(error, 'Failed to generate policies. Please try again.'));
      stopLoading();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setPrompt(suggestion);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const togglePolicySelection = (policyId: string | number) => {
    setSelectedPolicyIds(prev => {
      const next = new Set(prev);
      if (next.has(policyId)) {
        next.delete(policyId);
      } else {
        next.add(policyId);
      }
      return next;
    });
  };

  const selectAllPolicies = () => {
    const allIds = generatedPolicies.map(p => p.id).filter((id): id is string | number => id !== undefined);
    setSelectedPolicyIds(new Set(allIds));
  };

  const deselectAllPolicies = () => {
    setSelectedPolicyIds(new Set());
  };

  const clearAll = () => {
    setGeneratedPolicies([]);
    setSelectedPolicyIds(new Set());
    setShowSuggestions(true);
    setPrompt('');
  };

  const addSelectedPolicies = async () => {
    const policiesToAdd = generatedPolicies.filter(p => p.id !== undefined && selectedPolicyIds.has(p.id));
    if (policiesToAdd.length === 0) {
      toast.error('Please select at least one policy to add');
      return;
    }

    if (!tenantName || !cloudId) {
      toast.error('Please select a tenant and resource first');
      return;
    }

    setIsSaving(true);
    
    try {
      const uniqueAlertIds = new Set<string>();
      const policiesWithGeneratedLinks = policiesToAdd.map(policy => {
        const policyLinkedAlerts = Array.isArray(policy.linkedAlerts)
          ? policy.linkedAlerts.map(id => String(id)).filter(Boolean)
          : [];
        const selectedExistingAlerts = preLinkedAlerts.map(id => String(id)).filter(Boolean);
        const linkedAlerts = Array.from(new Set([...policyLinkedAlerts, ...selectedExistingAlerts]));

        if (linkedAlerts.length > 0) {
          linkedAlerts.forEach(id => uniqueAlertIds.add(id));
          return { ...policy, linkedAlerts };
        }

        if (selectedAlertId && generatedAlerts[selectedAlertId]) {
          uniqueAlertIds.add(String(selectedAlertId));
          return { ...policy, linkedAlerts: [String(selectedAlertId)] };
        }

        return { ...policy, linkedAlerts: [] };
      });

      const alertsToSave: any[] = [];
      const generatedAlertIdsToSave: string[] = [];
      uniqueAlertIds.forEach(id => {
        if (generatedAlerts[id]) {
          generatedAlertIdsToSave.push(id);
          alertsToSave.push(buildAlertSavePayload(generatedAlerts[id]));
        }
      });

      const persistedAlertIdByGeneratedId = new Map<string, string>();
      if (alertsToSave.length > 0) {
        const savedAlerts = await saveAlerts({
          alerts: alertsToSave,
          cloudType: selectedCloud,
          tenantName,
          cloudAccountId: cloudId
        });

        generatedAlertIdsToSave.forEach((generatedId, index) => {
          const savedAlertId = savedAlerts[index]?.alertId;
          if (savedAlertId) {
            persistedAlertIdByGeneratedId.set(generatedId, savedAlertId);
          }
        });

        const missingAlertIds = generatedAlertIdsToSave.filter(id => !persistedAlertIdByGeneratedId.has(id));
        if (missingAlertIds.length > 0) {
          throw new Error('One or more associated alerts could not be saved');
        }
      }

      const policiesToSave = policiesWithGeneratedLinks.map(policy => ({
        ...policy,
        linkedAlerts: (policy.linkedAlerts || []).map(alertId =>
          persistedAlertIdByGeneratedId.get(String(alertId)) || alertId
        ),
      }));

      const savedPolicies = await savePolicy({
        policies: policiesToSave.map(p => {
          const { id, ...policyForSave } = p as any;
          const numericId = typeof id === 'number' ? { id } : {};
          return {
            ...policyForSave,
            ...numericId,
            tenantName,
            cloudId: cloudId,
            cloudAccountId: cloudId,
          };
        }),
        cloudType: selectedCloud,
        tenantName,
        cloudAccountId: cloudId
      });

      // Remove added policies from generated list
      setGeneratedPolicies(prev => prev.filter(p => p.id === undefined || !selectedPolicyIds.has(p.id)));
      setSelectedPolicyIds(new Set());
      toast.success(`Added ${policiesToAdd.length} polic${policiesToAdd.length !== 1 ? 'ies' : 'y'} to your library`);
      
      // Refresh list
      onPoliciesAdded?.(savedPolicies.length > 0 ? savedPolicies : policiesToSave.map(p => normalizeCloudPolicy({ ...p, tenantName, cloudAccountId: cloudId })));
    } catch (error) {
      console.error('Failed to save policies:', error);
      toast.error('Failed to save policies. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const getColorClasses = (color: string, isSelected: boolean) => {
    const unselectedBg = 'bg-card backdrop-blur-xl';

    const colors: Record<string, { bg: string; border: string; icon: string; text: string }> = {
      amber: {
        bg: isSelected ? 'bg-amber-500/10' : unselectedBg,
        border: isSelected ? 'border-amber-500/50' : 'border-border',
        icon: isSelected ? 'bg-amber-500 text-white' : 'bg-muted text-muted-foreground',
        text: isSelected ? 'text-amber-600 dark:text-amber-400' : 'text-foreground',
      },
      rose: {
        bg: isSelected ? 'bg-rose-500/10' : unselectedBg,
        border: isSelected ? 'border-rose-500/50' : 'border-border',
        icon: isSelected ? 'bg-rose-500 text-white' : 'bg-muted text-muted-foreground',
        text: isSelected ? 'text-rose-600 dark:text-rose-400' : 'text-foreground',
      },
      blue: {
        bg: isSelected ? 'bg-blue-500/10' : unselectedBg,
        border: isSelected ? 'border-blue-500/50' : 'border-border',
        icon: isSelected ? 'bg-blue-500 text-white' : 'bg-muted text-muted-foreground',
        text: isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-foreground',
      },
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="space-y-6">
      {/* Policy Type Selector */}
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-3 gap-4">
          {policyTypes.map((type) => {
            const Icon = type.icon;
            const isSelected = selectedType === type.type;
            const colors = getColorClasses(type.color, isSelected);
            return (
              <button
                key={type.type}
                onClick={() => setSelectedType(type.type)}
                disabled={isLoading}
                className={`
                  p-5 rounded-2xl transition-all duration-300 text-center border flex flex-col items-center
                  ${colors.bg} ${colors.border}
                  portal-card-shadow
                  ${isSelected ? 'border-primary/40' : 'portal-card-hover'}
                  disabled:opacity-50 disabled:cursor-not-allowed
                `}
              >
                <div className={`
                  p-2.5 rounded-lg mb-3 transition-all duration-300
                  ${colors.icon}
                `}>
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className={`font-semibold text-sm transition-colors duration-300 ${colors.text}`}>
                  {type.label}
                </h3>
                <p className="text-xs text-muted-foreground mt-1">{type.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Pre-configure Policy Parameters */}
      <div className="flex flex-col gap-2">
        <CreatePolicySelectors
          tenantName={tenantName}
          cloudId={cloudId}
          selectedCloud={selectedCloud}
          selectedUsers={preSelectedUsers}
          selectedServiceAccounts={preSelectedServiceAccounts}
          selectedResources={preSelectedResources}
          onUsersChange={setPreSelectedUsers}
          onServiceAccountsChange={setPreSelectedServiceAccounts}
          onResourcesChange={setPreSelectedResources}
          disabled={isLoading || !tenantName || !cloudId}
        />

        {(selectedType === 'remediation' || selectedType === 'automatic') && (
          <PolicyAlertLinker
            tenantName={tenantName}
            cloudId={cloudId}
            selectedCloud={selectedCloud}
            selectedAlerts={preLinkedAlerts}
            onAlertsChange={setPreLinkedAlerts}
            disabled={isLoading || !tenantName || !cloudId}
          />
        )}
      </div>

      {/* Chat Input */}
      <div className="relative group">
        <div className="bg-card rounded-2xl p-3 border portal-card-elevated">
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <Sparkles className="h-4 w-4 text-primary/50" />
              </div>
              <input
                type="text"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Describe the policy you want to create..."
                className="w-full bg-transparent text-foreground placeholder:text-muted-foreground
                  focus:outline-none pl-9 pr-4 py-2 h-10 disabled:pointer-events-none"
                disabled={isPromptInputDisabled}
              />
            </div>
            <Button
              onClick={() => handleSubmit()}
              disabled={isSendPromptDisabled}
              size="icon"
              className="h-10 w-10 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground shrink-0 shadow-md hover:shadow-lg transition-all duration-200"
            >
              {(isLoading || isCreatePolicyTaskInProgress) ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>
        <p className="mt-2 px-1 text-[11px] text-muted-foreground">
          Each policy can change up to 100 entities and run up to 100 commands. Requests are checked for feasibility before a policy is generated.
        </p>
        {sendPromptBlockedReason && (
          <div className="pointer-events-none absolute left-1/2 top-0 z-50 hidden -translate-x-1/2 -translate-y-[calc(100%+8px)] whitespace-nowrap rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md group-hover:block group-focus-within:block">
            {sendPromptBlockedReason}
          </div>
        )}
      </div>

      {rejection && !isLoading && (
        <div className="animate-fade-in rounded-2xl border border-red-500/20 bg-card p-5 shadow-[0_4px_16px_rgb(239_68_68/0.08)]">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-red-500 text-white">
              <OctagonAlert className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">This policy can&apos;t be created</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    {rejection.reason || 'The request is not feasible within the policy limits.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setRejection(null)}
                  className="rounded-md p-1 text-muted-foreground hover:text-foreground"
                  aria-label="Dismiss"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              {(rejection.estimatedEntities || rejection.estimatedCommands) ? (
                <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                  {rejection.estimatedEntities ? (
                    <Badge variant="outline" className="bg-card">~{rejection.estimatedEntities} entities (limit 100)</Badge>
                  ) : null}
                  {rejection.estimatedCommands ? (
                    <Badge variant="outline" className="bg-card">~{rejection.estimatedCommands} commands (limit 100)</Badge>
                  ) : null}
                </div>
              ) : null}
              {rejection.suggestedPrompt && (
                <div className="mt-4 rounded-xl border border-border bg-card p-4 shadow-[0_2px_8px_rgb(15_23_42/0.04)]">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">Feasible alternative</p>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground">{rejection.suggestedPrompt}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      className="h-8 rounded-lg"
                      onClick={() => handleSubmit(rejection.suggestedPrompt)}
                      disabled={isPromptInputDisabled}
                    >
                      <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                      Generate this instead
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 rounded-lg"
                      onClick={() => {
                        setPrompt(rejection.suggestedPrompt || '');
                        setRejection(null);
                      }}
                    >
                      <CornerDownLeft className="mr-1.5 h-3.5 w-3.5" />
                      Edit before generating
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Suggestions */}
      {showSuggestions && !rejection && displayedSuggestions.length > 0 && !isLoading && (
        <div className="space-y-3">
          <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Suggestions for {selectedCloud} {selectedType} policies
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {displayedSuggestions.map((suggestion) => {
              const Icon = suggestion.icon;
              return (
                <button
                  key={`${selectedCloud}-${selectedType}-${suggestion.title}`}
                  onClick={() => handleSuggestionClick(suggestion.prompt)}
                  className="group relative text-left p-4 rounded-2xl bg-card backdrop-blur-xl border border-border
                    portal-card-shadow portal-card-hover overflow-hidden flex items-start gap-4"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="h-5 w-5" />
                  </div>
                  
                  <div className="flex-1 min-w-0 z-10">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <h4 className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors duration-200">{suggestion.title}</h4>
                      <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 -translate-x-2 translate-y-2 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-all duration-300 shrink-0" />
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{suggestion.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Loading Animation */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-12 w-full max-w-md">
            {/* Animated Shield */}
            <div className="relative w-36 h-36">
              <div className="absolute inset-0 rounded-full border-2 border-dashed border-primary/30 animate-[spin_8s_linear_infinite]" />
              <div className="absolute inset-3 rounded-full border-2 border-primary/40 animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite]" />
              <div className="absolute inset-6 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 animate-pulse" />
              <div className="absolute inset-6 rounded-full overflow-hidden">
                <div
                  className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/30 to-transparent animate-[scan_1.5s_ease-in-out_infinite]"
                  style={{ height: '200%', transform: 'translateY(-50%)' }}
                />
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative">
                  <Shield className="h-10 w-10 text-primary animate-pulse" />
                  <Sparkles className="absolute -top-1 -right-1 h-5 w-5 text-primary animate-bounce" />
                </div>
              </div>
              <div className="absolute inset-0 animate-[spin_3s_linear_infinite]">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-primary shadow-lg shadow-primary/50" />
              </div>
              <div className="absolute inset-0 animate-[spin_4s_linear_infinite_reverse]">
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-primary/70" />
              </div>
            </div>

            {/* Progress bar */}
            <div className="w-full space-y-3">
              <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ease-out ${
                    loadingProgress === 100
                      ? 'bg-green-500'
                      : 'bg-gradient-to-r from-primary via-primary/80 to-primary'
                  }`}
                  style={{ width: `${loadingProgress}%` }}
                />
              </div>
              <div className="flex justify-between items-center text-xs text-muted-foreground">
                <span>{loadingProgress}%</span>
                <span>{loadingProgress === 100 ? 'Complete' : 'Processing...'}</span>
              </div>
            </div>

            {/* Dynamic status text */}
            <div className="text-center space-y-2 w-full min-h-[60px]">
              <div className="flex items-center justify-center gap-2 text-sm font-medium text-foreground">
                <span key={loadingTextIndex} className="animate-fade-in">
                  {loadingProgress === 100 ? 'Generated!' : LOADING_TEXTS[loadingTextIndex] || LOADING_TEXTS[0]}
                </span>
                {loadingProgress < 100 && (
                  <span className="inline-flex">
                    <span className="animate-[bounce_1s_infinite_0ms]">.</span>
                    <span className="animate-[bounce_1s_infinite_200ms]">.</span>
                    <span className="animate-[bounce_1s_infinite_400ms]">.</span>
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground text-center">
                {loadingProgress === 100
                  ? 'Your custom policy is ready!'
                  : 'Generating enforcement rules for your cloud'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Generated Policies */}
      {generatedPolicies.length > 0 && !isLoading && (
        <div className="space-y-4 animate-fade-in">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <h3 className="text-base font-semibold text-foreground">Generated Policies</h3>
              <Badge variant="secondary" className="rounded-md text-xs">
                {selectedPolicyIds.size} / {generatedPolicies.length} selected
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={selectAllPolicies}
                className="text-xs h-8"
              >
                <Check className="h-3 w-3 mr-1" />
                Select All
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={deselectAllPolicies}
                className="text-xs h-8"
              >
                <X className="h-3 w-3 mr-1" />
                Deselect All
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAll}
                className="text-xs h-8 text-destructive hover:text-destructive"
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                Clear All
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {generatedPolicies.map((policy, index) => {
              const isSelected = policy.id !== undefined && selectedPolicyIds.has(policy.id);
              return (
                <div
                  key={policy.id}
                  style={{ animationDelay: `${index * 50}ms` }}
                  className={`relative animate-fade-in cursor-pointer rounded-2xl transition-all duration-300 ${
                    isSelected ? 'shadow-lg shadow-sky-500/5' : ''
                  }`}
                  onClick={() => policy.id !== undefined && togglePolicySelection(policy.id)}
                >
                  <div className={`absolute inset-0 rounded-2xl border-2 pointer-events-none transition-colors ${
                    isSelected ? 'border-primary' : 'border-transparent'
                  }`} />
                  <div className="absolute right-3 top-3 z-10">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => policy.id !== undefined && togglePolicySelection(policy.id)}
                      onClick={(e) => e.stopPropagation()}
                      className={`h-5 w-5 rounded-md border-2 shadow-sm ${
                        isSelected
                          ? 'bg-primary border-primary data-[state=checked]:bg-primary'
                          : 'bg-background border-border'
                      }`}
                    />
                  </div>

                  <PolicyCard
                    policy={policy}
                    onViewDetails={() => setPreviewPolicy(policy)}
                    onToggle={() => {}}
                    showToggle={false}
                  />
                </div>
              );
            })}
          </div>

          {/* Generated Alerts Section */}
          {Object.keys(generatedAlerts).length > 0 && (
            <div className="mt-8 space-y-4 animate-fade-in border-t border-border/40 pt-6">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center bg-primary/10 w-8 h-8 rounded-md">
                    <ShieldAlert className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground">Associated Alert Trigger</h3>
                    <p className="text-xs text-muted-foreground">Select one alert to link to the chosen policies</p>
                  </div>
                </div>
                <Badge variant="secondary" className="rounded-md text-xs">
                  {selectedAlertId ? '1' : '0'} / {Object.keys(generatedAlerts).length} selected
                </Badge>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {Object.values(generatedAlerts).map((alert, index) => {
                  const isSelected = selectedAlertId === alert.id;
                  return (
                    <div key={alert.id} style={{ animationDelay: `${index * 50}ms` }} className="animate-fade-in">
                      <AlertCard
                        alert={alert}
                        onViewDetails={(a) => setPreviewAlert(a)}
                        onToggle={() => {}}
                        hideSwitch={true}
                        selectable={true}
                        selected={isSelected}
                        onSelect={() => setSelectedAlertId(alert.id)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="sticky bottom-4 z-40 flex justify-end pt-4">
            <Button
              onClick={addSelectedPolicies}
              disabled={selectedPolicyIds.size === 0 || isSaving}
              className="h-11 gap-2 rounded-lg px-7 shadow-lg shadow-primary/20"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  Add {selectedPolicyIds.size} Selected {selectedPolicyIds.size !== 1 ? 'Policies' : 'Policy'} to Library
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Policy Details Modal - Create mode with editing */}
      <PolicyDetailsEdit
        policy={previewPolicy}
        open={previewPolicy !== null}
        onClose={() => setPreviewPolicy(null)}
        tenantName={tenantName}
        cloudId={cloudId}
        isCreateMode={true}
        linkedAlertDetails={generatedAlerts}
        onPolicyUpdate={(updatedPolicy) => {
          setGeneratedPolicies(prev => 
            prev.map(p => p.id === updatedPolicy.id ? normalizeCloudPolicy(updatedPolicy) : p)
          );
          setPreviewPolicy(null);
        }}
      />

      {/* Alert Details Edit Modal */}
      <AlertDetailsEdit
        alert={previewAlert}
        open={previewAlert !== null}
        onClose={() => setPreviewAlert(null)}
        tenantName={tenantName || ''}
        cloudId={cloudId || ''}
        selectedCloud={selectedCloud}
        isCreateMode={true}
        onAlertUpdate={(updatedAlert: any) => {
          setGeneratedAlerts(prev => ({
            ...prev,
            [updatedAlert.id]: updatedAlert
          }));
          setPreviewAlert(null);
        }}
      />
    </div>
  );
}
